// src/components/common/DisclaimerBanner.jsx
//
// Renders the admin-configured disclaimer message as a dismissible banner.
//

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, Bell, ChevronDown, ChevronUp } from "lucide-react";
import { useDisclaimer } from "../../context/DisclaimerContext";

const DisclaimerBanner = ({ screen }) => {
  const { showDisclaimer, content } = useDisclaimer();
  const [dismissed, setDismissed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Don't render if admin turned off this screen, user dismissed, or content is empty
  if (!showDisclaimer(screen) || dismissed || !content) return null;

  // Check if content length is long enough to offer expansion toggle
  const isLongContent = content.replace(/<[^>]*>/g, "").length > 160;

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.div
          initial={{ opacity: 0, y: -8, scale: 0.99 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.98 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="relative mb-4 overflow-hidden rounded-xl border border-amber-500/30 bg-card p-3.5 sm:p-4 shadow-lg transition-all duration-200"
          role="alert"
          aria-live="polite"
        >
          {/* Left Decorative Amber Bar */}
          <div className="absolute top-0 bottom-0 left-0 w-1 bg-amber-500 rounded-l-xl" />

          <div className="flex items-start gap-3 pl-1">
            {/* Warning Icon Box */}
            <div className="flex-shrink-0 mt-0.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 min-w-0 pr-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  <Bell className="w-3 h-3" />
                  Notice
                </span>
              </div>

              {/* Crisp High-Contrast Text Content */}
              <div
                className={`text-sm text-slate-100 leading-relaxed disclaimer-content ${
                  !isExpanded && isLongContent ? "line-clamp-2" : ""
                } [&_p]:mb-1 [&_p:last-child]:mb-0 [&_a]:text-amber-400 [&_a]:underline [&_strong]:font-semibold [&_strong]:text-amber-300`}
                dangerouslySetInnerHTML={{ __html: content }}
              />

              {/* Read Details Toggle for long content */}
              {isLongContent && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-amber-400 hover:text-amber-300 transition-colors"
                >
                  {isExpanded ? (
                    <>
                      Show Less <ChevronUp className="w-3 h-3" />
                    </>
                  ) : (
                    <>
                      Read Details <ChevronDown className="w-3 h-3" />
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={() => setDismissed(true)}
              className="flex-shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              aria-label="Dismiss disclaimer"
              title="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DisclaimerBanner;


